import type { SystemVersionDTO } from "@repo/codegen/teashop-base-api"
import type { IMockOptions } from "axios-plugins/plugins/mock"
import type { SensitiveApprovalDetailRow } from "@/features/sensitive-approval/types"
import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig, type AxiosResponse, type InternalAxiosRequestConfig } from "axios"
import { merge, mock, normalize, transform, useAxiosPlugin } from "axios-plugins"
import dayjs from "dayjs"
import { ElMessage, ElMessageBox } from "element-plus"
import { debounce, get, omitBy } from "lodash-es"
import { v4 as uuidv4 } from "uuid"
import { OPTION_ALL } from "@/constants"
import {
  isSensitiveApprovalDuplicatePendingMessage,
  isSensitiveApprovalSilentError,
  openSensitiveApprovalApplyDialog,
  SensitiveApprovalDuplicatePendingError,
  SensitiveApprovalSubmittedError,
} from "@/features/sensitive-approval/approvalDialog"
import { isPendingApproval, isRequireConfirmApproval } from "@/features/sensitive-approval/approvalGuards"
import {
  dispatchSensitiveApprovalChanged,
  isApprovalTerminalStatus,
  shouldOpenExistingApprovalReview,
} from "@/features/sensitive-approval/approvalPending"
import { APPROVAL_BUSINESS_NAME_HEADER, APPROVAL_REQUEST_ID_HEADER, APPROVAL_SUBMIT_HEADER, buildApprovalReplayConfig, SENSITIVE_APPROVAL_REPLAY_FLAG } from "@/features/sensitive-approval/approvalRequest"
import { COUNT_SESSION_MERGE_EXCLUSION } from "@/features/stock/countSessionTransport"
import { useSensitiveApprovalStoreHook } from "@/store/modules/sensitiveApproval"
import { useUserStoreHook } from "@/store/modules/user"
import { useViewScopeStoreHook } from "@/store/modules/viewScope"
import { appendApiGrayHeader, injectApiGrayHeaderDeps } from "@/utils/apiGrayHeader"
import {
  BusinessContextStaleRequestError,
  BusinessContextStaleResponseError,
  isBusinessContextStaleRequestError,
  isStaleAuthFailure,
  shouldCancelRequestAfterBusinessContextBarrier,
} from "@/utils/businessContext/authFailure"
import { invalidateBusinessContextRuntime } from "@/utils/businessContext/invalidation"
import { useBusinessContextRequestBarrier } from "@/utils/businessContext/requestBarrier"
import { resolveBusinessContextRequestVersion } from "@/utils/businessContext/requestVersion"
import { getBusinessContextRevision } from "@/utils/businessContext/revision"
import { invalidateSystemVersionRuntime } from "@/utils/businessContext/systemVersion"
import { buildLegacyRequestHeaders } from "@/utils/legacyRequestHeaders"
import {
  isRepoMockEnabled,
  REPO_MOCK_SERVER_URL,
  rewriteRepoMockRequestUrl,
} from "@/utils/repoMock"
import { getSenderId } from "@/utils/sender-id"
import {
  consumeExternalAuthTokenChange,
  markObservedAuthToken,
} from "@/utils/tokenScopeSession"
import { getToken, removeRefreshToken, removeToken } from "./cache/cookies"
import { replaceToLogin } from "./loginRedirect"

declare module "axios" {
  interface AxiosRequestConfig {
    /** 设为 true 时跳过业务 code 包装校验，调用方自行处理 response.data。 */
    skipBusinessCodeCheck?: boolean
    /** 设为 true 时跳过全局错误 toast，调用方自行展示错误。 */
    skipGlobalErrorToast?: boolean
    /**
     * 设为 true 时 401/424 不触发全局"登录过期"弹窗与踢出,错误交调用方处理。
     * 用于**后端尚未部署的新接口**先行联调:网关对不存在路径返 424/401 short-circuit,
     * 会把正常登录用户误踢出系统(2026-07-06 门店商品返现配置抽屉实测)。
     */
    skipAuthExpiredPrompt?: boolean
    /** axios-plugins mock 配置，单个接口显式开启后才走 repo mock-server。 */
    mock?: RepoMockRequestConfig["mock"]
    /**
     * 业务上下文初始化时显式指定目标机构版本。
     * `null` 表示版本发现请求，不继承当前机构的版本/灰度请求头。
     */
    businessContextSystemVersion?: SystemVersionDTO | null
    /** 业务上下文事务内部请求使用，避免等待自己持有的迁移屏障。 */
    skipBusinessContextBarrier?: boolean
    /** 请求真正发出时的认证 token 快照；仅供响应拦截器隔离旧会话副作用。 */
    authTokenAtDispatch?: string
    /** 请求真正发出时的业务上下文版本；用于识别 token A -> B -> A。 */
    businessContextRevisionAtDispatch?: number
    /** 登出等会话终止请求使用调用时捕获的 token，避免本地清理抢先剥离认证头。 */
    authTokenOverride?: string
    /** 登出等终止请求忽略本地会话已变化的响应隔离，响应仅交调用方收尾。 */
    ignoreBusinessContextStaleResponse?: boolean
  }
}

export { REPO_MOCK_SERVER_URL }

export interface SensitiveApprovalRequestConfig {
  sensitiveApprovalBusinessName?: string
  sensitiveApprovalDetailRows?: SensitiveApprovalDetailRow[]
  sensitiveApprovalReason?: string
  sensitiveApprovalAutoSubmit?: boolean
}

interface RepoMockRequestConfig {
  mock?: boolean | Pick<IMockOptions, 'mock' | 'mockUrl'>
}

type ServiceInternalRequestConfig = InternalAxiosRequestConfig & RepoMockRequestConfig
type ServiceAxiosError = AxiosError<Record<string, any>> & { msg?: string | null }

const repoMockEnabled = isRepoMockEnabled(import.meta.env)

// 依赖注入 getter
let getApiVersion: ((url: string, systemVersion?: SystemVersionDTO | null) => string | undefined) | undefined
let getIsGray: ((systemVersion?: SystemVersionDTO | null) => boolean | undefined) | undefined

// 全局设置一个统一的处理函数，用于处理被中断的请求
const handleRequestAborted = (error: any) => {
  // 请求中止错误，不显示任何提示
  if (error.code === 'ECONNABORTED' || error.message === 'Request aborted') {
    console.warn('请求被中止:', error.config?.url)
    return Promise.resolve({ data: { code: -1, msg: '请求被中止' } })
  }
  return Promise.reject(error)
}

function getRequestHeader(config: AxiosRequestConfig, name: string) {
  const headers = config.headers as any
  if (!headers) {
    return undefined
  }
  if (typeof headers.get === 'function') {
    return headers.get(name)
  }
  return headers[name] ?? headers[name.toLowerCase()]
}

function isApprovalReplayConfig(config: AxiosRequestConfig) {
  return Boolean((config as any)[SENSITIVE_APPROVAL_REPLAY_FLAG])
}

function extractApprovalPayload(result: any) {
  if (result && typeof result === 'object' && result.data && typeof result.data === 'object' && 'code' in result.data) {
    return result.data.data
  }
  if (result && typeof result === 'object' && 'code' in result && 'data' in result) {
    return result.data
  }
  return result
}

function shouldUseLocalApprovalBusinessName(payload: any, businessName?: string) {
  if (!payload || typeof payload !== 'object' || !businessName) {
    return false
  }
  const remoteBusinessName = payload.businessName
  if (!remoteBusinessName) {
    return true
  }
  return String(remoteBusinessName) === String(payload.businessId)
}

function applyLocalApprovalBusinessName<T>(payload: T, config?: AxiosRequestConfig): T {
  const businessName = getLocalApprovalBusinessName(config)
  if (!shouldUseLocalApprovalBusinessName(payload, businessName)) {
    return payload
  }
  return {
    ...(payload as any),
    businessName,
  }
}

function normalizeApprovalText(value: unknown) {
  if (value === undefined || value === null || value === '') {
    return ''
  }
  return String(value).trim()
}

function normalizeApprovalNumber(value: unknown) {
  if (value === undefined || value === null || value === '') {
    return undefined
  }
  const normalized = Number(value)
  return Number.isFinite(normalized) ? normalized : undefined
}

function formatApprovalAmountFromCent(value: unknown) {
  const normalized = normalizeApprovalNumber(value)
  if (normalized === undefined) {
    return normalizeApprovalText(value)
  }
  return `¥${(normalized / 100).toFixed(2)}`
}

function formatApprovalDiscountRate(value: unknown) {
  const rate = normalizeApprovalNumber(value)
  if (rate === undefined || rate <= 0) {
    return ''
  }
  const displayedRate = rate <= 1 ? rate * 10 : (rate <= 10 ? rate : rate / 10)
  return `${Number(displayedRate.toFixed(2))}折`
}

function approvalDetailRow(label: string, value: unknown): SensitiveApprovalDetailRow | null {
  const normalizedValue = normalizeApprovalText(value)
  return normalizedValue ? { label, value: normalizedValue } : null
}

function normalizeApprovalDetailRows(rows: Array<null | SensitiveApprovalDetailRow | undefined>) {
  const seen = new Set<string>()
  return rows
    .filter(Boolean)
    .map(row => ({
      label: normalizeApprovalText(row?.label),
      value: normalizeApprovalText(row?.value),
    }))
    .filter(row => row.label && row.value && row.value !== '--')
    .filter(row => {
      const key = `${row.label}:${row.value}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
}

function parseRequestData(config?: AxiosRequestConfig) {
  const data = config?.data
  if (!data || typeof data !== 'string') {
    return data && typeof data === 'object' ? data as Record<string, any> : {}
  }
  try {
    return JSON.parse(data)
  } catch (_error) {
    return {}
  }
}

function buildDiscountApprovalRows(discountInfo: Record<string, any> = {}) {
  const discountType = normalizeApprovalNumber(discountInfo.discountType)
  const discountAmount = normalizeApprovalNumber(discountInfo.discountAmount)
  const discountRate = normalizeApprovalNumber(discountInfo.discountRate)
  const isRateDiscount = discountType === 1 || discountType === 41
  const isWaiver = discountType === 2 || discountType === 42 || discountType === 11
  return [
    isRateDiscount && discountRate !== undefined
      ? approvalDetailRow('折扣率', formatApprovalDiscountRate(discountRate))
      : null,
    isWaiver && discountAmount !== undefined
      ? approvalDetailRow(discountType === 11 ? '抹零金额' : '减免金额', formatApprovalAmountFromCent(Math.abs(discountAmount)))
      : null,
    discountAmount !== undefined
      ? approvalDetailRow('优惠金额', formatApprovalAmountFromCent(Math.abs(discountAmount)))
      : null,
    approvalDetailRow('申请原因', discountInfo.discountDescription || discountInfo.discountReason),
  ]
}

function getLocalApprovalDetailRows(config?: AxiosRequestConfig): SensitiveApprovalDetailRow[] {
  const explicitRows = normalizeApprovalDetailRows((config as SensitiveApprovalRequestConfig | undefined)?.sensitiveApprovalDetailRows || [])
  if (explicitRows.length) {
    return explicitRows
  }

  const data = parseRequestData(config)
  const params = config?.params as Record<string, any> | undefined
  const itemOrder = data.itemOrder || {}
  const discountInfo = data.discountInfo || {}
  const discountList = Array.isArray(data.discountList) ? data.discountList : []
  const count = normalizeApprovalNumber(itemOrder.count)
  const salePrice = normalizeApprovalNumber(itemOrder.salePrice)
  const itemAmount = count !== undefined && salePrice !== undefined ? count * salePrice : undefined
  const totalDiscountAmount = discountList.reduce((total: number, item: Record<string, any>) => {
    const amount = normalizeApprovalNumber(item?.discountAmount)
    return amount === undefined ? total : total + Math.abs(amount)
  }, 0)
  const url = normalizeApprovalText(config?.url)
  return normalizeApprovalDetailRows([
    approvalDetailRow('商品', itemOrder.itemName || itemOrder.name || data.itemName || data.productName),
    count !== undefined ? approvalDetailRow('数量', `${count}`) : null,
    itemAmount !== undefined ? approvalDetailRow('涉及金额', formatApprovalAmountFromCent(itemAmount)) : null,
    ...buildDiscountApprovalRows(discountInfo),
    totalDiscountAmount ? approvalDetailRow('优惠金额', formatApprovalAmountFromCent(totalDiscountAmount)) : null,
    ...discountList.flatMap((item: Record<string, any>) => buildDiscountApprovalRows(item)),
    url.includes('cancel') ? null : approvalDetailRow('备注', data.remarks),
    approvalDetailRow('取消原因', data.remarks || data.cancelReason || params?.cancelReason),
  ])
}

function applyLocalApprovalContext<T>(payload: T, config?: AxiosRequestConfig): T {
  const payloadWithBusinessName = applyLocalApprovalBusinessName(payload, config)
  if (!payloadWithBusinessName || typeof payloadWithBusinessName !== 'object') {
    return payloadWithBusinessName
  }
  const approvalDetailRows = getLocalApprovalDetailRows(config)
  const defaultApplicationReason = getLocalApprovalDefaultReason(config)
  if (!approvalDetailRows.length && !defaultApplicationReason) {
    return payloadWithBusinessName
  }
  return {
    ...(payloadWithBusinessName as Record<string, unknown>),
    ...(approvalDetailRows.length ? { approvalDetailRows } : {}),
    ...(defaultApplicationReason ? {
      defaultApplicationReason,
      defaultApplicationReasonEnabled: true,
    } : {}),
  } as T
}

function failClosedOnExternalAuthTokenChange(currentToken?: string) {
  if (!consumeExternalAuthTokenChange(currentToken ?? "")) {
    return
  }
  const userStore = useUserStoreHook()
  const viewScopeStore = useViewScopeStoreHook()
  invalidateBusinessContextRuntime(
    { userStore, viewScopeStore, invalidateSystemVersionRuntime },
    { requestRecovery: true },
  )
  ElMessage.warning("登录权限已更新，请重新选择机构后继续操作")
  throw new Error("AUTH_TOKEN_SCOPE_CHANGED")
}

export function clearExpiredLoginSession() {
  const userStore = useUserStoreHook()
  const viewScopeStore = useViewScopeStoreHook()
  invalidateBusinessContextRuntime({
    userStore,
    viewScopeStore,
    invalidateSystemVersionRuntime,
  })
  removeToken()
  removeRefreshToken()
  markObservedAuthToken("")
  viewScopeStore.reset()
}

function getLocalApprovalBusinessName(config?: AxiosRequestConfig) {
  const directName = (config as SensitiveApprovalRequestConfig | undefined)?.sensitiveApprovalBusinessName?.trim()
  if (directName) {
    return directName
  }
  const headerName = getRequestHeader(config ?? {}, APPROVAL_BUSINESS_NAME_HEADER)
  if (typeof headerName !== 'string' || !headerName.trim()) {
    return ''
  }
  try {
    return decodeURIComponent(headerName).trim()
  } catch (_error) {
    return headerName.trim()
  }
}

function getLocalApprovalReason(config?: AxiosRequestConfig) {
  const reason = (config as SensitiveApprovalRequestConfig | undefined)?.sensitiveApprovalReason?.trim()
  return reason || ''
}

function shouldUseLocalApprovalReasonAsDefault(config?: AxiosRequestConfig) {
  const url = normalizeApprovalText(config?.url)
  return isCancelReasonApprovalRequest(url)
}

function getLocalApprovalDefaultReason(config?: AxiosRequestConfig) {
  if (!shouldUseLocalApprovalReasonAsDefault(config)) {
    return ''
  }
  return getLocalApprovalReason(config)
}

function isCancelReasonApprovalRequest(url: string) {
  return url.includes('merchant_oms_item_order/cancel') || url.includes('/merchant/table/record/cancel/')
}

function shouldAutoSubmitApproval(config?: AxiosRequestConfig) {
  const reason = getLocalApprovalReason(config)
  if (!reason) {
    return false
  }
  const explicitAutoSubmit = Boolean((config as SensitiveApprovalRequestConfig | undefined)?.sensitiveApprovalAutoSubmit)
  const url = normalizeApprovalText(config?.url)
  return explicitAutoSubmit || isCancelReasonApprovalRequest(url)
}

function syncAutoSubmittedApproval(payload: any) {
  if (!isPendingApproval(payload)) {
    return
  }
  const approvalStore = useSensitiveApprovalStoreHook()
  const ticket = approvalStore.upsertFromPayload(payload)
  if (ticket && !isApprovalTerminalStatus(ticket.status)) {
    approvalStore.showFloatingTicket(ticket.ticketId)
  }
  if (ticket && shouldOpenExistingApprovalReview(payload)) {
    approvalStore.requestReview(ticket.ticketId)
    ElMessage.info(payload.message || "该操作已有待审核申请，请审核原申请")
  } else if (payload.existingTicket === true) {
    ElMessage.warning(payload.message || "该操作已有待审核申请，请等待审核结果")
  }
  dispatchSensitiveApprovalChanged(payload)
}

async function submitApprovalWithoutDialog(service: AxiosInstance, config: AxiosRequestConfig, approvalPayload: any) {
  const replayResult = await service(buildApprovalReplayConfig(config, approvalPayload, getLocalApprovalReason(config)))
  const replayApprovalPayload = applyLocalApprovalContext(extractApprovalPayload(replayResult), config)
  if (!isPendingApproval(replayApprovalPayload)) {
    return replayResult
  }
  syncAutoSubmittedApproval(replayApprovalPayload)
  if (replayApprovalPayload.status === 'EXECUTED') {
    return replayResult
  }
  if (isApprovalTerminalStatus(replayApprovalPayload.status)) {
    return Promise.reject(new Error("审核已通过，但敏感操作执行失败，请刷新后确认"))
  }
  return Promise.reject(new SensitiveApprovalSubmittedError(replayApprovalPayload))
}

export function injectServiceDeps(deps: {
  getApiVersion?: (version: string, systemVersion?: SystemVersionDTO | null) => string | undefined
  getIsGray?: (systemVersion?: SystemVersionDTO | null) => boolean | undefined
}) {
  if (deps.getApiVersion) getApiVersion = deps.getApiVersion
  if (deps.getIsGray) {
    getIsGray = deps.getIsGray
    injectApiGrayHeaderDeps({ getIsGray: () => deps.getIsGray?.() })
  }
}

/** 创建请求实例 */
function createService(options?: { fullResponse: boolean, overwriteToken?: boolean }) {
  // 创建一个 Axios 实例
  const request = axios.create()
  const service = useAxiosPlugin(request)
    .plugin(
      mock({
        enable: repoMockEnabled,
        // 默认不代理请求；单个接口显式设置 config.mock.mock 才会走 mock-server。
        mock: false,
        mockUrl: REPO_MOCK_SERVER_URL,
      }),
    )
    .plugin(
      merge({
        includes: "/",
        excludes: [
          COUNT_SESSION_MERGE_EXCLUSION,
          // 如果购物车更新接口刚被调用，然后点击点单，点单成功后也会调用更新购物车接口，可能被限流导致没有清空购物车
          // https://devops.aliyun.com/projex/bug/RUSR-1466# 《【B端管理系统-需更改-线上】桌台订单-已选商品区域-商品点单后-未清空已点商品》
          /teashop_merchant\/merchant\/table_cart\/set\/.*\/2b/,
          // 本地打印 claim/complete 是状态机推进接口；回放已完成响应会让同一 claimToken 重复打印。
          /teashop_merchant\/merchant_print_task\/local\/(?:claim(?:\/v[234](?:\/batch)?)?|priority\/(?:heartbeat|presence)|(?:deliveries\/)?\d+\/complete)/,
          // FormData is serialized as `{}` by the merge hash below. Consecutive
          // package-room image uploads must remain distinct requests and responses.
          /\/admin\/reservation-table-profiles\/images(?:\?|$)/,
          // Settings forms lock while saving. Replaying a recently failed write
          // prevents immediate retry; multipart image bodies also hash to `{}`.
          /(?:^|\/)teashop_merchant\/merchant_shop(?:\?|$)/,
          /(?:^|\/)teashop_merchant\/merchant_shop\/(?:update_reservable_setting|update_status\/\d+\/\d+)(?:\?|$)/,
          /(?:^|\/)teashop_merchant\/merchant\/pms\/item\/upload(?:\?|$)/,
          /(?:^|\/)admin\/reservation-table-profiles\/\d+(?:\?|$)/,
          // Report retry must fetch again instead of replaying a failed response.
          /(?:^|\/)admin\/hq-reports\/reservations\/(?:summary|details)\/query(?:\?|$)/,
        ],
        delay: 500,
        calcRequstHash: (config) => {
          // GET方法直接调用
          if (config.method === "GET") {
            return uuidv4()
          }
          // 其他方法会merge
          return JSON.stringify({
            method: config.method ?? "",
            url: config.url ?? "",
            params: config.params ?? {},
            data: config.data,
            approvalSubmit: getRequestHeader(config, APPROVAL_SUBMIT_HEADER),
            approvalRequestId: getRequestHeader(config, APPROVAL_REQUEST_ID_HEADER),
          })
        },
      }),
    )
    .plugin(
      // JZTODO 增加金额转换插件、日期转换插件
      transform({
        // JZTODO 检查是否有意外的影响 #important 优化此处逻辑，不合理
        request: (config) => {
          const whiteList = ['categoryId']
          config.params = omitBy(config.params, (value, key) => {
            return value === undefined || value === null || (value === OPTION_ALL && !whiteList.includes(key)) || key === "dateValue"
          })
          return config
        },
        // + 转换响应参数
        response: (res) => {
          return res
        },
        // + 转换异常信息
        capture: (e) => {
          throw e
        },
      }),
    )
    .plugin(
      normalize({
        // 过滤url
        url: {
          // 过滤url中, 重复的 `//`, 如: `/api/a//b` -> `/api/a/b`
          noDuplicateSlash: true,
        },
        data: {},
        params: {
          noUndefined: true,
        },
      }),
    )
    .wrap()

  // 对本地时间和服务器时间进行检查
  const showTimeLag = debounce((serverTime: Date, localTime: Date) => {
    const timeLag = dayjs(localTime).diff(dayjs(serverTime), 'minutes')
    // 设置时间差大于 5 分钟时，弹出提示框
    if (Math.abs(timeLag) >= 5) {
      ElMessageBox.alert(`设备与服务器时间相差: ${timeLag} 分钟，请更新你的设备时间，避免数据异常`, '提示')
    }
  }, 1000 * 60 * 3) // 设置防抖时间为 3 分钟

  // 请求拦截
  service.interceptors.request.use(
    async (config: ServiceInternalRequestConfig) => {
      failClosedOnExternalAuthTokenChange(getToken())
      const authTokenBeforeBarrier = getToken() ?? ""
      const revisionBeforeBarrier = getBusinessContextRevision()
      if (!config.skipBusinessContextBarrier) {
        const barrierResolution = await useBusinessContextRequestBarrier()
          .waitForBusinessContextReady()
        const authTokenAfterBarrier = getToken() ?? ""
        const revisionAfterBarrier = getBusinessContextRevision()
        if (shouldCancelRequestAfterBusinessContextBarrier({
          authTokenBeforeBarrier,
          authTokenAfterBarrier,
          revisionBeforeBarrier,
          revisionAfterBarrier,
          requestPolicy: barrierResolution.requestPolicy,
        })) {
          throw new BusinessContextStaleRequestError()
        }
        failClosedOnExternalAuthTokenChange(authTokenAfterBarrier)
      }
      let shopId = -1
      let userId = -1
      try {
        shopId = useUserStoreHook().shopId
        userId = useUserStoreHook().getUserInfo()?.corpUserId ?? -1
      } catch (error) {
        console.log(error)
      }
      const authorization = getRequestHeader(config, "Authorization")
      if (
        config.authTokenOverride !== undefined
        || options?.overwriteToken
        || (typeof authorization === "string" && authorization.startsWith("Bearer "))
      ) {
        const token = config.authTokenOverride ?? getToken()
        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`
        } else {
          delete config.headers["Authorization"]
        }
      }
      config.authTokenAtDispatch = config.authTokenOverride ?? getToken() ?? ""
      config.businessContextRevisionAtDispatch = getBusinessContextRevision()
      const approvalSubmit = getRequestHeader(config, APPROVAL_SUBMIT_HEADER)
      const approvalRequestId = getRequestHeader(config, APPROVAL_REQUEST_ID_HEADER)
      const approvalRequestKey = approvalSubmit ? `,approval:${approvalSubmit}:${approvalRequestId ?? ''}` : ''
      config.headers["X-Request-ID"] = `${shopId},${userId},${config.url},${config.method}${approvalRequestKey}`
      config.headers["X-Sender"] = getSenderId() ?? ""
      // 添加请求时间，方便调试
      config.headers["A-Request-Date"] = new Date().toISOString()
      // 如果 mockUrl 配置为 @repo/mock-server 标识，则交给同源 mock 承接。
      if (typeof config.mock === 'object' && config.mock.mock && repoMockEnabled) {
        const mockUrl = config.mock.mockUrl ?? REPO_MOCK_SERVER_URL
        const repoMockUrl = rewriteRepoMockRequestUrl({
          baseApi: import.meta.env.VITE_BASE_API,
          mockUrl,
          url: config.url,
        })
        if (repoMockUrl) {
          config.mock.mockUrl = REPO_MOCK_SERVER_URL
          // 使用同源 mock 中间件或静态 Service Worker，因此需要重写为空 baseURL。
          config.baseURL = ''
          config.url = repoMockUrl
        }
      }
      // 上下文切换请求必须显式携带目标机构版本，不能读取旧机构的全局版本。
      const requestVersion = resolveBusinessContextRequestVersion(
        config.businessContextSystemVersion,
      )
      if (getApiVersion) {
        const apiVersion = getApiVersion(config.url ?? '', requestVersion.version)
        if (apiVersion) {
          config.headers["X-API-Version"] = apiVersion
        } else {
          delete config.headers["X-API-Version"]
        }
      }
      if (requestVersion.mode === "target") {
        appendApiGrayHeader(
          config.headers as Record<string, unknown>,
          getIsGray?.(requestVersion.version),
        )
      } else if (requestVersion.mode === "inherit") {
        appendApiGrayHeader(config.headers as Record<string, unknown>)
      }
      return config
    },
    // 发送失败
    (error: AxiosError) => Promise.reject(error),
  )
  // 响应拦截（可根据具体业务作出相应的调整）
  service.interceptors.response.use(
    async (response: AxiosResponse) => {
      if (!response.config.ignoreBusinessContextStaleResponse && isStaleAuthFailure(
        response.config.authTokenAtDispatch,
        getToken() ?? "",
        response.config.businessContextRevisionAtDispatch,
        getBusinessContextRevision(),
      )) {
        return Promise.reject(new BusinessContextStaleResponseError())
      }
      showTimeLag(new Date(response.headers['date']), new Date())
      const fullResponseData = options?.fullResponse ? response : response.data as any

      // apiData 是 API 返回的数据
      const apiData = response.data as any
      // 这个 Code 是和后端约定的业务 Code
      const code = apiData.code
      const access_token = apiData.access_token
      const isReplay = isApprovalReplayConfig(response.config)
      if (response.config.skipBusinessCodeCheck) {
        return fullResponseData
      }
      // 如果没有 Code, 代表这不是项目后端开发的 API
      if (access_token !== undefined) {
        return apiData
      }
      if (code === undefined) {
        ElMessage.error("非本系统的接口")
        return Promise.reject(new Error("非本系统的接口"))
      } else {
        switch (code) {
          case 0: {
            const approvalPayload = applyLocalApprovalContext(apiData.data, response.config)
            apiData.data = approvalPayload
            if (isRequireConfirmApproval(approvalPayload)) {
              if (isReplay) {
                const message = approvalPayload.message || "审核申请提交失败，请稍后重试"
                return Promise.reject(new Error(message))
              }
              if (shouldAutoSubmitApproval(response.config)) {
                return submitApprovalWithoutDialog(service, response.config, approvalPayload)
              }
              try {
                const replayResult = await openSensitiveApprovalApplyDialog(approvalPayload, async (reason) => {
                  return service(buildApprovalReplayConfig(response.config, approvalPayload, reason))
                })
                const replayApprovalPayload = applyLocalApprovalContext(extractApprovalPayload(replayResult), response.config)
                if (isPendingApproval(replayApprovalPayload)) {
                  return Promise.reject(new SensitiveApprovalSubmittedError(replayApprovalPayload))
                }
                return replayResult
              } catch (error) {
                if (isSensitiveApprovalSilentError(error)) {
                  return Promise.reject(error)
                }
                return Promise.reject(error)
              }
            }
            if (isPendingApproval(approvalPayload)) {
              if (isReplay) {
                return fullResponseData
              }
              return Promise.reject(new SensitiveApprovalSubmittedError(approvalPayload))
            }
            // code === 0 代表没有错误
            return fullResponseData
          }
          default: {
            // 不是正确的 Code
            const message = apiData.msg || "Error"
            if (isReplay && isSensitiveApprovalDuplicatePendingMessage(message)) {
              return Promise.reject(new SensitiveApprovalDuplicatePendingError(message))
            }
            if (!isReplay && !response.config.skipGlobalErrorToast) {
              ElMessage.error(message)
            }
            return Promise.reject(new Error(message))
          }
        }
      }
    },
    (error: ServiceAxiosError) => {
      if (isBusinessContextStaleRequestError(error)) {
        error.msg = null
        return Promise.reject(error)
      }
      // 首先处理请求中止的情况
      if (error.code === 'ECONNABORTED' || error.message === 'Request aborted') {
        return handleRequestAborted(error)
      }
      if (!error.config?.ignoreBusinessContextStaleResponse && isStaleAuthFailure(
        error.config?.authTokenAtDispatch,
        getToken() ?? "",
        error.config?.businessContextRevisionAtDispatch,
        getBusinessContextRevision(),
      )) {
        error.msg = null
        return Promise.reject(error)
      }

      // Status 是 HTTP 状态码
      const status = get(error, "response.status")
      const requestUrl = String(get(error, "config.url") ?? "")
      if (
        (status === 401 || status === 424)
        && !error.config?.ignoreBusinessContextStaleResponse
        && isStaleAuthFailure(
          error.config?.authTokenAtDispatch,
          getToken() ?? "",
          error.config?.businessContextRevisionAtDispatch,
          getBusinessContextRevision(),
        )
      ) {
        error.msg = null
        return Promise.reject(error)
      }
      if (error.code === "ERR_NETWORK") {
        return Promise.reject(error)
      }
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        return Promise.reject(error)
      }
      switch (status) {
        case 400:
          error.msg = "网络异常，请稍后尝试"
          break
        case 401:
          // 不能登录
          // SLICE-005 FU-A 修复:**不再自动调 logoutApi** — 避免触发 dev OAuth grant cache 死锁
          //   (SLICE-001/003/004 多次踩;详 slice-dev-guide.md §三 / §八 / be CLAUDE.md FU-A backlog)
          //   旧:useUserStoreHook().logout() + location.reload()  → 调 logoutApi 导致全账号 OAuth 挂半小时
          //   新:仅清本地 token cookie + 提示用户点重新登录;不调任何 auth API
          if (requestUrl.indexOf("auth/oauth/token") > -1 && requestUrl.indexOf("PHONE") > -1) {
            error.msg = "验证码错误"
          } else if (requestUrl.indexOf("auth/oauth/token") > -1 && requestUrl.indexOf("WX_SCAN") > -1) {
            error.msg = "暂无登录权限"
          } else if (error.config?.skipAuthExpiredPrompt) {
            // 未部署新接口豁免(同 424 分支):不清 token 不跳登录,调用方自行降级
            error.msg = null
          } else {
            // 把后端 data 字段的真实错误透出来(GlobalExceptionHandler 把异常吞到 data 字段,详 slice-dev-guide §二)
            const dataMessage = error.response?.data?.data
            const msgFallback = error.response?.data?.msg || "登录信息无效,请重新登录"
            const displayMsg = typeof dataMessage === "string" && dataMessage
              ? dataMessage
              : msgFallback
            error.msg = null // 抑制 line 441 默认 toast,避免重复
            ElMessage.error({ message: displayMsg, duration: 5000 })
            // 仅清本地 token,不调 logoutApi(根因 FU-A)
            clearExpiredLoginSession()
            // 跳登录页;不强制 reload(避免 SPA 状态丢失影响其他 tab)
            replaceToLogin()
          }
          break
        case 424:
          // token过期，无效
          // 未部署新接口的网关 short-circuit 豁免:调用方自行处理,不踢出正常登录用户
          if (error.config?.skipAuthExpiredPrompt) {
            error.msg = null
            break
          }
          // 2026-07-06 多标签误伤修复:外部 token 变化(另一标签重登/续期)触发 fail-closed
          // 清 scope 引导重选机构后,在途请求的 424 是预期噪音 — 不弹"过期"不踢出,
          // 交由机构重选流程接管;真 token 过期(scope 仍在)照旧弹窗。
          if (!useViewScopeStoreHook().activeScopeId) {
            error.msg = null
            break
          }
          error.msg = null
          ElMessageBox.alert("登录信息已过期，请重新登录", "提示", {
            confirmButtonText: "确定",
            callback: () => {
              if (isStaleAuthFailure(
                error.config?.authTokenAtDispatch,
                getToken() ?? "",
                error.config?.businessContextRevisionAtDispatch,
                getBusinessContextRevision(),
              )) {
                return
              }
              clearExpiredLoginSession()
              replaceToLogin()
            },
          })
          break
        case 403:
          // 有有效token，但是没权限
          error.msg = "没有足够权限进行此操作"
          break
        case 404:
          error.msg = "请求地址出错"
          break
        case 408:
          error.msg = "请求超时"
          break
        case 500:
          error.msg = "网络异常，请稍后尝试"
          break
        case 501:
          error.msg = "服务未实现"
          break
        case 502:
          error.msg = "网关错误"
          break
        case 503:
          error.msg = "服务不可用"
          break
        case 504:
          error.msg = "网关超时"
          break
        case 505:
          error.msg = "HTTP 版本不受支持"
          break
        default:
          if (error.response?.data?.msg) {
            error.msg = error.response.data.msg
          } else {
            error.msg = error.message || "网络异常，请稍后尝试"
          }
          break
      }
      if (error.msg && !error.config?.skipGlobalErrorToast) {
        ElMessage.error(error.msg)
      }
      return Promise.reject(error)
    },
  )
  return service
}

/**
 * @deprecated 请使用 @repo/codegen 封装后的 API 代替
 * 创建请求方法
 */
function createRequestFunction(service: AxiosInstance) {
  return function <T>(config: AxiosRequestConfig & { customHeaders?: Record<string, string> } & SensitiveApprovalRequestConfig): Promise<T> {
    const headers = buildLegacyRequestHeaders(config, {
      approvalBusinessNameHeader: APPROVAL_BUSINESS_NAME_HEADER,
      zodanceVersion: import.meta.env.VITE_ZODANCE_VERSION,
      token: getToken(),
    }) as any
    const dataSource = import.meta.env.VITE_DATA_SOURCE
    if (dataSource) {
      headers["X-DataSource"] = dataSource
    }
    const configDefault = {
      headers,
      timeout: 30000,
      baseURL: import.meta.env.VITE_BASE_API,
      data: {},
    }
    const { customHeaders: _customHeaders, headers: _headers, ...requestConfig } = config
    return service({
      ...configDefault,
      ...requestConfig,
      headers,
    })
  }
}

/**
 * @deprecated 请使用 service2 代替
 * 用于网络请求的实例
 */
export const service = createService()

/**
 * 用于网络请求的实例
 */
export const service2 = createService({ fullResponse: true, overwriteToken: true })
/**
 * @deprecated 请使用 @repo/codegen 封装后的 API 代替
 * 用于网络请求的方法
 */
export const request = createRequestFunction(service)
